import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppState } from '../../contexts/AppStateContext';
import FileUpload from '../common/FileUpload';
import ToolHeader from '../common/ToolHeader';
import ProgressBar from '../common/ProgressBar';
import { compressPdf } from '../../utils/pdfCompressionUtils';
import { formatFileSize, downloadFile } from '../../utils/fileUtils';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiDownload, FiLoader, FiInfo, FiAlertCircle } = FiIcons;

const PdfCompressTool = () => {
  const { t } = useLanguage();
  const { state, dispatch } = useAppState();
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');

  const handleFileUpload = (file) => {
    if (file.type !== 'application/pdf') {
      if (window.showToast) {
        window.showToast(t('invalidFileType'), 'error');
      }
      return;
    }
    dispatch({ type: 'SET_SELECTED_FILE', payload: file });
  };

  const handleCompress = async () => {
    if (!state.selectedFile) {
      if (window.showToast) {
        window.showToast(t('pleaseUploadFile'), 'warning');
      }
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      setProgress(0);
      setProgressMessage('開始壓縮...');

      const result = await compressPdf(
        state.selectedFile,
        state.compressionLevel,
        (prog, msg) => {
          setProgress(prog);
          setProgressMessage(msg);
        }
      );

      dispatch({
        type: 'SET_COMPRESSED_PDF',
        payload: {
          blob: result.blob,
          url: result.url,
          size: result.size
        }
      });

      if (window.showToast) {
        window.showToast(t('compressionSuccess'), 'success');
      }
    } catch (error) {
      console.error('PDF 壓縮錯誤:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message });
      if (window.showToast) {
        window.showToast(error.message || t('error'), 'error');
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
      setProgress(0);
      setProgressMessage('');
    }
  };

  const handleDownload = () => {
    if (state.compressedPdfBlob) {
      const originalName = state.selectedFile.name.split('.')[0];
      downloadFile(state.compressedPdfBlob, `${originalName}-compressed.pdf`);
    }
  };

  if (state.showResults && state.compressedPdfUrl) {
    const savedSpace = state.originalSize - state.processedSize;
    const ratio = Math.round((1 - (state.processedSize / state.originalSize)) * 100);

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ToolHeader title={t('compressionResults')} />

        <div className="p-6">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-6 mb-6 border border-green-200">
            <div className="flex items-center gap-2 mb-4">
              <SafeIcon icon={FiInfo} className="text-green-600" />
              <h4 className="font-semibold text-green-800">壓縮成功!</h4>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('originalFile')}</span>
                <p className="font-semibold text-lg text-gray-800">
                  {formatFileSize(state.originalSize)}
                </p>
              </div>

              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('compressedFile')}</span>
                <p className="font-semibold text-lg text-green-600">
                  {formatFileSize(state.processedSize)}
                </p>
              </div>

              <div className="bg-white/60 rounded-lg p-4">
                <span className="text-sm text-gray-600 block mb-1">{t('spaceSaved')}</span>
                <p className="font-semibold text-lg text-green-600">
                  {formatFileSize(savedSpace)}
                  <span className="text-sm ml-2">({ratio}%)</span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <motion.button
              className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center gap-2 shadow-md"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleDownload}
            >
              <SafeIcon icon={FiDownload} />
              {t('downloadCompressedPdf')}
            </motion.button>

            <motion.button
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => dispatch({ type: 'RESET_TOOL' })}
            >
              {t('startOver')}
            </motion.button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <ToolHeader
        title={t('pdfCompress')}
        filename={state.selectedFile?.name}
      />

      <div className="p-6">
        <p className="text-gray-600 mb-6">
          {t('pdfCompressDesc')}
        </p>

        {!state.showSettings ? (
          <FileUpload
            onFileUpload={handleFileUpload}
            acceptedTypes="application/pdf"
            uploadText={t('dropOrClickPdf')}
            supportText={t('supportPdf')}
          />
        ) : (
          <div className="space-y-6">
            {state.selectedFile && (
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-600">{t('fileSize')}:</span>
                    <span className="ml-2 font-medium">
                      {formatFileSize(state.originalSize)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-800 mb-4">
                {t('compressionSettings')}
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('compressionLevel')}
                  </label>
                  <select
                    value={state.compressionLevel}
                    onChange={(e) => dispatch({
                      type: 'UPDATE_SETTINGS',
                      payload: { compressionLevel: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="low">{t('lowCompression')}</option>
                    <option value="medium">{t('mediumCompression')}</option>
                    <option value="high">{t('highCompression')}</option>
                  </select>

                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                    <SafeIcon icon={FiInfo} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-800">
                      {t('pdfCompressionNote')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <AnimatePresence>
              {state.isLoading && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="bg-purple-50 rounded-lg p-4"
                >
                  <ProgressBar
                    progress={progress}
                    message={progressMessage}
                    showPercentage={true}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex gap-3 justify-center">
              <motion.button
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
                whileHover={{ scale: state.isLoading ? 1 : 1.02 }}
                whileTap={{ scale: state.isLoading ? 1 : 0.98 }}
                onClick={handleCompress}
                disabled={state.isLoading}
              >
                {state.isLoading ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    {t('compressingPleaseWait')}
                  </>
                ) : (
                  t('compressPdf')
                )}
              </motion.button>

              <motion.button
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => dispatch({ type: 'RESET_TOOL' })}
              >
                {t('reuploadFile')}
              </motion.button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default PdfCompressTool;